import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const FORM_NAME = "SBIKYC1";
const PDF_ENDPOINT = "/FillMyForm/SBIKYC1/Print.php";
const LOOKUP_ENDPOINT = "/FillMyForm/SBIKYC1/ajaxfile.php";

const fields = [
  {
    name: "acno",
    label: "Account Number",
    maxLength: 11,
    numeric: true,
    required: true,
  },
  {
    name: "branch",
    label: "Branch Name",
    maxLength: 25,
    required: true,
  },
  {
    name: "pan",
    label: "PAN Number",
    maxLength: 10,
    uppercase: true,
  },
  {
    name: "name",
    label: "Applicant Name",
    maxLength: 40,
    alphaOnly: true,
    required: true,
  },
  {
    name: "aadhaar",
    label: "Aadhaar Number",
    maxLength: 12,
    numeric: true,
    required: true,
  },
  {
    name: "add1",
    label: "Address Line 1",
    maxLength: 35,
    required: true,
  },
  {
    name: "add2",
    label: "Address Line 2",
    maxLength: 35,
    required: true,
  },
  {
    name: "city",
    label: "City",
    maxLength: 15,
    required: true,
  },
  {
    name: "pin",
    label: "PIN",
    maxLength: 6,
    numeric: true,
    required: true,
  },
  {
    name: "mobile",
    label: "Mobile",
    maxLength: 10,
    numeric: true,
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    maxLength: 30,
  },
  {
    name: "occupation",
    label: "Occupation",
    maxLength: 36,
  },
  {
    name: "income",
    label: "Income",
    maxLength: 36,
    numeric: true,
  },
];

const initialFormData = fields.reduce(
  (data, field) => ({
    ...data,
    [field.name]: "",
  }),
  {},
);

const tabs = ["Applicant Details", "Preview"];

function validateField(field, value) {
  const text = String(value ?? "").trim();

  if (field.required && !text) {
    return `${field.label} is required.`;
  }

  if (
    field.alphaOnly &&
    text &&
    !/^[A-Za-z ]+$/.test(text)
  ) {
    return `${field.label} must contain alphabet letters only.`;
  }

  if (field.name === "acno" && text && !/^\d{11}$/.test(text)) {
    return "Account number must contain 11 digits.";
  }

  if (field.name === "aadhaar" && text && !/^\d{12}$/.test(text)) {
    return "Aadhaar number must contain exactly 12 digits.";
  }

  if (field.name === "pin" && text && !/^\d{6}$/.test(text)) {
    return "PIN must contain exactly 6 digits.";
  }

  if (field.name === "mobile" && text && !/^\d{10}$/.test(text)) {
    return "Mobile number must contain 10 digits.";
  }

  if (
    field.name === "pan" &&
    text &&
    !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(text)
  ) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }

  if (
    field.type === "email" &&
    text &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
  ) {
    return "Enter a valid email address.";
  }

  return "";
}

async function readJsonResponse(response) {
  const text = await response.text();

  if (!text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error("The server returned an invalid JSON response.");
  }
}

export default function KYC1() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupMessage, setLookupMessage] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  useEffect(() => {
    if (currentTab !== 0) return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById("acno")?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentTab]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAutofill() {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(FORM_NAME)}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        const result = await readJsonResponse(response);

        if (
          !response.ok ||
          !result?.success ||
          !result?.found ||
          !result?.data
        ) {
          return;
        }

        setFormData((previous) => ({
          ...previous,
          branch: previous.branch || result.data.branch || "",
          city: previous.city || result.data.city || "",
          pin: previous.pin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("KYC autofill error:", error);
        }
      }
    }

    loadAutofill();

    return () => controller.abort();
  }, []);

  useEffect(
    () => () => {
      if (pdfUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    },
    [pdfUrl],
  );

  const updateField = (field, rawValue) => {
    let value = rawValue;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    if (field.uppercase || field.name.includes("pan")) {
      value = value.toUpperCase();
    }

    setFormData((previous) => ({
      ...previous,
      [field.name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field.name]: "",
    }));

    if (field.name === "acno") {
      setLookupMessage("");
    }
  };

  const lookupAccount = async () => {
    const accountNumber = formData.acno.trim();

    if (!accountNumber) {
      setErrors((previous) => ({
        ...previous,
        acno: "Account Number is required.",
      }));
      return;
    }

    if (accountNumber.length !== 11) {
      setErrors((previous) => ({
        ...previous,
        acno: "Account number must contain 11 digits.",
      }));
      return;
    }

    setLookupLoading(true);
    setLookupMessage("");

    try {
      const body = new URLSearchParams();
      body.set("acno", accountNumber);

      const response = await fetch(LOOKUP_ENDPOINT, {
        method: "POST",
        body,
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          Accept: "application/json",
        },
      });

      const result = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(`Account lookup failed (${response.status}).`);
      }

      if (
        !result ||
        result.Boolen === "False" ||
        (Array.isArray(result) && result.length === 0)
      ) {
        setLookupMessage("No saved customer details found.");
        return;
      }

      const row = Array.isArray(result) ? result[0] : result;
      const address = String(row.add || "");
      const addressLine1 = address.slice(0, 30);
      const addressLine2 = address.slice(30, 60);

      setFormData((previous) => ({
        ...previous,
        name: row.custname || row.name || previous.name,
        add1: addressLine1 || row.add1 || previous.add1,
        add2: addressLine2 || row.add2 || previous.add2,
        mobile: row.mobile || previous.mobile,
      }));

      setLookupMessage("Saved customer details loaded.");
    } catch (error) {
      console.error("KYC account lookup error:", error);
      setLookupMessage(error.message || "Unable to load account details.");
    } finally {
      setLookupLoading(false);
    }
  };

  const validateApplicantTab = () => {
    const nextErrors = {};

    fields.forEach((field) => {
      const message = validateField(field, formData[field.name]);

      if (message) {
        nextErrors[field.name] = message;
      }
    });

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const goNext = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!validateApplicantTab()) {
      return;
    }

    setCurrentTab(1);
    setMaxAllowedTab(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevious = () => {
    setCurrentTab(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    if (
      fieldName === fields[fields.length - 1].name &&
      event.key === "Tab" &&
      !event.shiftKey
    ) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!validateApplicantTab()) {
      setCurrentTab(0);
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();

      Object.entries(formData).forEach(([name, value]) => {
        body.append(name, String(value ?? ""));
      });

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        60000,
      );

      let response;

      try {
        response = await fetch(PDF_ENDPOINT, {
          method: "POST",
          body,
          credentials: "include",
          headers: {
            Accept: "application/pdf",
          },
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!response.ok) {
        const message = await response.text();

        throw new Error(
          message || `PDF generation failed (${response.status}).`,
        );
      }

      const contentType =
        response.headers.get("content-type") || "";

      if (!contentType.includes("application/pdf")) {
        const message = await response.text();

        throw new Error(
          message || "Print.php did not return a PDF file.",
        );
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setPdfUrl((previous) => {
        if (previous?.startsWith("blob:")) {
          URL.revokeObjectURL(previous);
        }

        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      console.error("SBI KYC PDF error:", error);

      if (error.name === "AbortError") {
        setPdfError(
          "PDF generation timed out. Check whether Print.php is holding the PHP session.",
        );
      } else {
        setPdfError(
          error.message || "Unable to generate PDF preview.",
        );
      }
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && (
            <span className="text-red-500"> *</span>
          )}
        </Label>

        <div
          className={
            field.name === "acno"
              ? "flex items-start gap-2"
              : ""
          }
        >
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            maxLength={field.maxLength}
            placeholder={field.label}
            className={error ? "border-red-500" : ""}
            onChange={(event) =>
              updateField(field, event.target.value)
            }
            onBlur={
              field.name === "acno"
                ? lookupAccount
                : undefined
            }
            onKeyDown={(event) =>
              handleLastFieldKeyDown(event, field.name)
            }
          />

          {field.name === "acno" && (
            <Button
              type="button"
              variant="outline"
              onClick={lookupAccount}
              disabled={lookupLoading}
            >
              {lookupLoading ? "Loading..." : "Load"}
            </Button>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        {field.name === "acno" && lookupMessage && (
          <p className="text-sm text-muted-foreground">
            {lookupMessage}
          </p>
        )}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI KYC Form"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 SBI KYC Form
            </CardTitle>

            <p className="text-muted-foreground">
              Enter the account and customer KYC details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-2 gap-3">
          {tabs.map((tab, index) => (
            <Button
              key={tab}
              type="button"
              variant={
                currentTab === index ? "default" : "outline"
              }
              disabled={index > maxAllowedTab}
              onClick={() => setCurrentTab(index)}
            >
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        {currentTab === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>👤 Applicant Details</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {fields.map(renderField)}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>📋 Preview</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="overflow-hidden rounded-lg border">
                {fields.map((field) => (
                  <div
                    key={field.name}
                    className="grid grid-cols-2 border-b p-3 last:border-b-0"
                  >
                    <span className="font-medium">
                      {field.label}
                    </span>

                    <span className="break-words">
                      {formData[field.name] || "-"}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {pdfError && (
          <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
            {pdfError}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={currentTab === 0}
          >
            ← Previous
          </Button>

          {currentTab === tabs.length - 1 ? (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading
                ? "Generating PDF..."
                : "Submit & Preview PDF"}
            </Button>
          ) : (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={goNext}
            >
              Preview
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
